/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable jsx-a11y/no-static-element-interactions */
import React, { Component } from "react"
import PropTypes from "prop-types"
import classNames from "classnames"
import { Tooltip } from "@quorumus/plum"

// components
import { ExpandableDropdown } from "app/static/frontend/search-new/components/FilterGroup/ExpandableDropdown"
import { Icon } from "quorumdesign"

import { mobileBreakpoint } from "app/static/frontend/design-constants"

export default class FilterGroup extends Component {
    state = {}

    static propTypes = {
        alwaysOpen: PropTypes.bool,
        renderedFilters: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.func, PropTypes.object])).isRequired,
        uniqueFilterGroupId: PropTypes.string.isRequired,
        uniqueFlvId: PropTypes.string.isRequired,
        hasActiveFilter: PropTypes.bool, // .isRequired,
        displayLabel: PropTypes.string,
        noHeader: PropTypes.bool,
        isOpen: PropTypes.bool, // .isRequired,
        // from shared/search/containers/FilterGroup/index.js
        openFilterGroup: PropTypes.func.isRequired,
        toggleFilterGroup: PropTypes.func.isRequired,
        helpText: PropTypes.string,
        isCustom: PropTypes.bool,
        isProfile: PropTypes.bool,
        invisible: PropTypes.bool,
        dataCy: PropTypes.string,
        // selected from the search => props.uniqueFlvId slice of the store
        searchFilterTerm: PropTypes.string,
        searchifyHeaderName: PropTypes.string,
    }

    // getDerivedStateFromProps is invoked right before calling the render method,
    // both on the initial mount and on subsequent updates.
    // It should return an object to update the state, or null to update nothing.
    // This method exists for rare use cases where the state depends on changes in props over time.
    // reactjs.org/docs/react-component.html#static-getderivedstatefromprops

    // getDerivedStateFromProps is static to help ensure purity
    // (no access to instance properties),
    // which reduces risk when writing async-compatible React components.
    // https://twitter.com/dan_abramov/status/979745329649213440
    // https://stackoverflow.com/a/57950655/6201291
    // https://github.com/reactjs/rfcs/pull/6/files/b7189ccf42c5f8c039d268841efdfda296eec411#diff-5e06005dbc26fcac5989745eaca87e80R161
    static getDerivedStateFromProps(props, state) {
        // we want to open the FilterGroup when props.searchFilterTerm matches this.props.displayLabel
        // but state.filterGroupContainsSearchFilterTerm is false
        // (i.e., when the FilterGroup ExpandableDropdown title matches the search term,
        // but none of its FilterComponent children do)
        if (
            props.searchFilterTerm &&
            !state.filterGroupContainsSearchFilterTerm &&
            props.displayLabel &&
            props.displayLabel.toLowerCase().includes(props.searchFilterTerm.toLowerCase())
        ) {
            props.openFilterGroup()

            return {
                // we use filterGroupContainsSearchFilterTerm to denote that either the
                // FilterComponent or ExpandableDropdown contain the searchFilterTerm
                filterGroupContainsSearchFilterTerm: true,
                // we use filterGroupFilterChildrenContainSearchFilterTerm to denote that at least one of the
                // FilterComponent children contain the searchFilterTerm
                filterGroupFilterChildrenContainSearchFilterTerm: false,
                searchFilterTerm: props.searchFilterTerm,
                shouldUnmountFilterGroup: false,
            }
        }
        if (
            // when we clear this.props.searchFilterTerm in the parent component,
            // we want to ensure that we reset this.state.filterGroupContainsSearchFilterTerm
            // before the next FilterGroup render() is called
            (!props.searchFilterTerm && state.filterGroupContainsSearchFilterTerm) ||
            // when the searchFilterTerm changes, reset the state of filterGroupContainsSearchFilterTerm
            // so it can be recomputed in each FilterComponent that re-renders
            (props.searchFilterTerm && props.searchFilterTerm !== state.searchFilterTerm)
        ) {
            return {
                filterGroupContainsSearchFilterTerm: false,
                filterGroupFilterChildrenContainSearchFilterTerm: false,
                searchFilterTerm: props.searchFilterTerm,
                shouldUnmountFilterGroup: false,
            }
        }

        return {
            searchFilterTerm: props.searchFilterTerm,
        }
    }

    componentDidMount() {
        this.checkShouldUnmountFilterGroup()
    }

    componentDidUpdate() {
        this.checkShouldUnmountFilterGroup()
    }

    checkShouldUnmountFilterGroup = () => {
        if (
            // when we have a search term
            this.props.searchFilterTerm &&
            // and we have not hidden the renderFilters FilterComponents yet
            !this.state.shouldUnmountFilterGroup &&
            // and none of the current renderFilters FilterComponents contain the search term
            !this.state.filterGroupContainsSearchFilterTerm
        ) {
            // unmount the renderFilters FilterComponent children
            // until we clear the current search term
            // or fire a new search
            this.setState({
                shouldUnmountFilterGroup: true,
            })
        }
    }

    getTooltip() {
        return (
            <Tooltip label={this.props.helpText} withinPortal={false}>
                <Icon icon="info-circle" iconFamily="far" />
            </Tooltip>
        )
    }

    setFilterGroupContainsSearchFilterTerm = (filterGroupContainsSearchFilterTerm) => {
        if (filterGroupContainsSearchFilterTerm) {
            this.setState({
                filterGroupContainsSearchFilterTerm: true,
                // we use filterGroupFilterChildrenContainSearchFilterTerm to denote that at least one of the
                // FilterComponent children contain the searchFilterTerm
                filterGroupFilterChildrenContainSearchFilterTerm: true,
            })
            this.props.openFilterGroup()
        }
    }

    renderFilters() {
        return this.props.renderedFilters.map((item) => {
            const FilterComponent = item
            // Construct a unique key
            // uniqueFilterId is a Redux key and is not always unique, since filters may share state. For example, Party
            // is divided into two lists, to save space. The first list is a set of checkboxes of major parties, and the
            // second an autocomplete dropdown of obscure parties.
            // Therefore, they add a componentKey for the sake of uniqueness while rendering.
            const key = `${item.defaultProps.uniqueFilterId}${item.defaultProps.componentKey || ""}`

            return (
                <FilterComponent
                    key={key}
                    searchFilterTerm={this.props.searchFilterTerm}
                    searchifyHeaderName={this.props.searchifyHeaderName}
                    setFilterGroupContainsSearchFilterTerm={this.setFilterGroupContainsSearchFilterTerm}
                    shouldAlwaysDisplayFilter={
                        // when we have a search term,
                        this.props.searchFilterTerm &&
                        this.props.displayLabel &&
                        // the ExpandableDropdown title contains the search term
                        this.props.displayLabel.toLowerCase().includes(this.props.searchFilterTerm.toLowerCase()) &&
                        // and none of the current renderFilters FilterComponent children contain the search term
                        !this.state.filterGroupFilterChildrenContainSearchFilterTerm
                    }
                    uniqueFilterGroupId={this.props.uniqueFilterGroupId}
                    uniqueFlvId={this.props.uniqueFlvId}
                    isProfile={this.props.isProfile}
                />
            )
        })
    }

    render() {
        const isMobile = window.innerWidth <= mobileBreakpoint

        return !this.props.noHeader ? (
            <ExpandableDropdown
                alwaysOpen={this.props.alwaysOpen}
                dataCy={this.props.dataCy}
                filterGroupContainsSearchFilterTerm={this.state.filterGroupContainsSearchFilterTerm}
                hasActiveChild={this.props.hasActiveFilter}
                helpText={this.props.helpText}
                isExpanded={this.props.isOpen}
                searchFilterTerm={this.props.searchFilterTerm}
                shouldUnmountFilterGroup={this.state.shouldUnmountFilterGroup}
                title={this.props.displayLabel}
                toggleFilterGroup={this.props.toggleFilterGroup}
                tooltipElement={!isMobile && this.getTooltip()}
            >
                {this.renderFilters()}
            </ExpandableDropdown>
        ) : (
            // keeping old code for filters with noHeader for now
            <div
                className={classNames("panel", "panel-default", {
                    "filter-group": !this.props.alwaysOpen,
                    "always-open": this.props.alwaysOpen,
                })}
                data-cy={this.props.dataCy}
            >
                {this.props.isOpen && this.renderFilters()}
            </div>
        )
    }
}

FilterGroup.propTypes = {}
